import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { brandAssets, company, social } from "@/data/brand";
import { services } from "@/data/services";
import { MessengerIcon, WhatsAppIcon } from "@/components/FloatingCTA";

export default function Footer() {
  const { t, lang } = useI18n();
  const featured = services.slice(0, 5);

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/[0.06] bg-zinc-950/60 mt-8">
      <div className="container-mt py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 md:gap-8">
        {/* Brand + location */}
        <div className="lg:col-span-4">
          <img src={brandAssets.logoWordmark} alt="MAD TURBO" className="h-9 mb-5" />
          <p className="font-mono uppercase tracking-[0.28em] text-brand-lime text-[10px] mb-6">
            {t("footer.tagline")}
          </p>
          <div className="space-y-2.5 text-sm text-zinc-400">
            <div className="flex items-center gap-3">
              <MapPin size={14} className="text-brand-lime shrink-0" />
              <span>{lang === "lt" ? company.location.lt : company.location.en}</span>
            </div>
            <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-3 hover:text-white transition-colors">
              <Phone size={14} className="text-brand-lime shrink-0" />
              <span className="font-mono">{company.phone}</span>
            </a>
            <a href={`mailto:${company.email2}`} className="flex items-center gap-3 hover:text-white transition-colors">
              <Mail size={14} className="text-brand-lime shrink-0" />
              <span>{company.email2}</span>
            </a>
          </div>
        </div>

        {/* Navigation */}
        <div className="lg:col-span-2">
          <h4 className="eyebrow mb-5">{t("footer.navigation")}</h4>
          <ul className="space-y-2.5 text-[14px] text-zinc-400">
            {["home","about","services","vehicles","partners","process","faq","contact"].map((k) => (
              <li key={k}>
                <Link to={k === "home" ? "/" : `/${k}`} className="hover:text-white transition-colors">
                  {t(`nav.${k}`)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Services */}
        <div className="lg:col-span-3">
          <h4 className="eyebrow mb-5">{t("footer.servicesCol")}</h4>
          <ul className="space-y-2.5 text-[14px] text-zinc-400">
            {featured.map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="hover:text-white transition-colors">
                  {lang === "lt" ? s.title.lt : s.title.en}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/services" className="inline-flex items-center gap-1.5 text-brand-lime hover:gap-2 transition-all">
                {t("common.readMore")} <ArrowRight size={12} />
              </Link>
            </li>
          </ul>
        </div>

        {/* Get in touch — direct-contact panel */}
        <div className="lg:col-span-3">
          <h4 className="eyebrow mb-5">{t("footer.getInTouch")}</h4>
          <p className="text-sm text-zinc-400 mb-5 leading-relaxed">
            {t("footer.getInTouchText")}
          </p>
          <Link
            to="/contact"
            data-testid="footer-contact-cta"
            className="btn-primary w-full justify-center mb-3"
          >
            {t("footer.bookConsultCta")} <ArrowRight size={14} />
          </Link>
          <div className="grid grid-cols-2 gap-2">
            <a
              href={social.whatsapp}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="footer-whatsapp-cta"
              className="btn-outline !py-2.5 justify-center text-[11px]"
            >
              <WhatsAppIcon size={13} /> WhatsApp
            </a>
            <a
              href={social.messenger}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="footer-messenger-cta"
              className="btn-outline !py-2.5 justify-center text-[11px]"
            >
              <MessengerIcon size={13} /> Messenger
            </a>
          </div>

          <div className="mt-8">
            <p className="eyebrow mb-3">{t("footer.followUs")}</p>
            <div className="flex gap-2">
              <a href={social.facebook} aria-label="Facebook" data-testid="social-facebook" target="_blank" rel="noreferrer noopener" className="w-9 h-9 rounded-full border border-zinc-800 hover:border-brand-lime text-zinc-400 hover:text-brand-lime flex items-center justify-center transition-colors"><Facebook size={14} /></a>
              <a href={social.instagram} aria-label="Instagram" data-testid="social-instagram" target="_blank" rel="noreferrer noopener" className="w-9 h-9 rounded-full border border-zinc-800 hover:border-brand-lime text-zinc-400 hover:text-brand-lime flex items-center justify-center transition-colors"><Instagram size={14} /></a>
              <a href={social.messenger} aria-label="Messenger" data-testid="social-messenger" target="_blank" rel="noreferrer noopener" className="w-9 h-9 rounded-full border border-zinc-800 hover:border-brand-lime text-zinc-400 hover:text-brand-lime flex items-center justify-center transition-colors"><MessengerIcon size={14} /></a>
              <a href={social.whatsapp} aria-label="WhatsApp" data-testid="social-whatsapp" target="_blank" rel="noreferrer noopener" className="w-9 h-9 rounded-full border border-zinc-800 hover:border-brand-lime text-zinc-400 hover:text-brand-lime flex items-center justify-center transition-colors"><WhatsAppIcon size={14} /></a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/[0.06]">
        <div className="container-mt py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-[11px] text-zinc-500 font-mono uppercase tracking-[0.22em]">
            {t("common.copyright", { year: new Date().getFullYear() })}
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-[11px] text-zinc-500 font-mono uppercase tracking-[0.22em]">
            <Link to="/privacy" className="hover:text-white transition-colors">{t("footer.privacy")}</Link>
            <Link to="/cookies" className="hover:text-white transition-colors">{t("footer.cookies")}</Link>
            <Link to="/terms" className="hover:text-white transition-colors">{t("footer.terms")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
